// src/components/job-engine/RecurringListPage.tsx
import React, { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import {
  Stack, Text, PrimaryButton, MessageBar, MessageBarType,
  DetailsList, SelectionMode, IColumn, Toggle, IconButton,
  Dialog, DialogType, DialogFooter, DefaultButton,
} from "@fluentui/react";
import { isAxiosError } from "axios";
import { useRecurring } from "./useRecurring";
import { useJobEngineConnectionState } from "./useJobEngineConnectionState";
import ConnectionStatusBadge from "./ConnectionStatusBadge";
import { toggleRecurring, triggerRecurringNow } from "@/api/jobEngine";
import { formatDateTime } from "./formatters";
import type { RecurringJob } from "@/api/jobEngine";

const RecurringListPage: React.FC = () => {
  const navigate = useNavigate();
  const { items, isLoading, error, refetch } = useRecurring();
  const isConnected = useJobEngineConnectionState();
  const [status, setStatus] = useState<{ type: "success" | "error"; text: string } | null>(null);
  const [confirmTriggerId, setConfirmTriggerId] = useState<string | null>(null);

  const setStatusMessage = (err: unknown, fallback: string) => {
    if (isAxiosError(err) && err.response?.status === 403) {
      setStatus({ type: "error", text: "You don't have permission for this action." });
    } else if (isAxiosError(err)) {
      setStatus({ type: "error", text: err.response?.data?.title ?? fallback });
    } else {
      setStatus({ type: "error", text: "An unexpected error occurred." });
    }
  };

  const onToggle = async (id: string, nextEnabled: boolean) => {
    setStatus(null);
    try {
      await toggleRecurring(id, nextEnabled);
      setStatus({ type: "success", text: nextEnabled ? "Schedule enabled" : "Schedule disabled" });
      refetch();
    } catch (err) {
      setStatusMessage(err, "Toggle failed");
    }
  };

  const onTrigger = async (id: string) => {
    setStatus(null);
    try {
      await triggerRecurringNow(id);
      setStatus({ type: "success", text: "Job queued" });
      refetch();
    } catch (err) {
      setStatusMessage(err, "Trigger failed");
    } finally {
      setConfirmTriggerId(null);
    }
  };

  const columns: IColumn[] = [
    { key: "name", name: "Name", fieldName: "name", minWidth: 160 },
    { key: "jobType", name: "Type", fieldName: "jobType", minWidth: 140 },
    { key: "cron", name: "Cron", fieldName: "cron", minWidth: 160 },
    { key: "nextFireUtc", name: "Next fire", minWidth: 180, onRender: (r: RecurringJob) => formatDateTime(r.nextFireUtc) },
    { key: "enabled", name: "Enabled", minWidth: 80, onRender: (r: RecurringJob) => (
      <Toggle checked={r.enabled} onChange={(_, v) => onToggle(r.recurringId, Boolean(v))} />
    ) },
    { key: "actions", name: "", minWidth: 110, onRender: (r: RecurringJob) => (
      <Stack horizontal tokens={{ childrenGap: 4 }}>
        <IconButton iconProps={{ iconName: "PlayerPlay" }} title="Trigger now" onClick={() => setConfirmTriggerId(r.recurringId)} />
        <Link to={`/job-engine/recurring/${encodeURIComponent(r.recurringId)}`}>Edit</Link>
      </Stack>
    ) },
  ];

  return (
    <div style={{ height: "100%", overflowY: "auto", overflowX: "hidden" }}>
    <Stack tokens={{ childrenGap: 16 }}>
      <div className="page-header">
        <h2 className="page-title">Recurring schedules</h2>
        <Stack horizontal tokens={{ childrenGap: 8 }} verticalAlign="center">
          <PrimaryButton text="New" onClick={() => navigate("/job-engine/recurring/new")} />
          <ConnectionStatusBadge isConnected={isConnected} />
        </Stack>
      </div>

      {status && (
        <MessageBar messageBarType={status.type === "success" ? MessageBarType.success : MessageBarType.error}>
          {status.text}
        </MessageBar>
      )}
      {!isConnected && (
        <MessageBar messageBarType={MessageBarType.warning}>
          Live updates unavailable — polling.
        </MessageBar>
      )}
      {error && <MessageBar messageBarType={MessageBarType.error}>{error}</MessageBar>}

      <DetailsList items={items} columns={columns} selectionMode={SelectionMode.none} compact={true} />
      {!isLoading && items.length === 0 && <Text>No schedules yet.</Text>}

      <Dialog
        hidden={!confirmTriggerId}
        onDismiss={() => setConfirmTriggerId(null)}
        dialogContentProps={{ type: DialogType.normal, title: "Trigger now?", subText: "This will queue a one-off run outside the schedule." }}
      >
        <DialogFooter>
          <PrimaryButton text="Trigger" onClick={() => confirmTriggerId && onTrigger(confirmTriggerId)} />
          <DefaultButton text="Cancel" onClick={() => setConfirmTriggerId(null)} />
        </DialogFooter>
      </Dialog>
    </Stack>
    </div>
  );
};

export default RecurringListPage;
